import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../Firebase';
import { collection, getDocs } from 'firebase/firestore';
import { FiMapPin, FiGlobe, FiUser, FiClock, FiX } from 'react-icons/fi';
import './DoctorsPage.css';

const DoctorsPage = () => {
  const navigate = useNavigate();
  const [doctorsList, setDoctorsList] = useState([]);
  const [selectedDoctorModal, setSelectedDoctorModal] = useState(null);

  // Fetch from Firestore
  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'doctors'));
        if (!querySnapshot.empty) {
          const list = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setDoctorsList(list);
        }
      } catch (err) {
        console.error("Error fetching doctors from Firestore: ", err);
      }
    };
    fetchDoctors();
  }, []);

  // Separate head doctor (id: 1) from remaining doctors, fallback to first in list if id '1' is deleted
  const headDoctor = doctorsList.find((d) => String(d.id) === '1') || doctorsList[0];
  const otherDoctors = headDoctor ? doctorsList.filter((d) => d.id !== headDoctor.id) : [];

  const openModal = (doctor) => setSelectedDoctorModal(doctor);
  const closeModal = () => setSelectedDoctorModal(null);

  return (
    <div className="doctors-page-wrapper">
      {/* Header Section */}
      <header
        className="doctors-header"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(10, 36, 34, 0.52) 0%, rgba(10, 36, 34, 0.62) 100%), url(${process.env.PUBLIC_URL}/assets/images/img13.jpg)`,
        }}
      >
        <div className="doctors-header-overlay" />
        <div className="doctors-header-content">
          <h1>Meet Our Doctors</h1>
          <p>
            Connect with our experienced team of specialists dedicated to providing compassionate, ethical and personalized healthcare across multiple medical specialties.
          </p>
        </div>
      </header>

      <div className="doctors-section-container">

        {doctorsList.length === 0 && (
          <div style={{ padding: '60px', textAlign: 'center', color: '#64748b' }}>
            <div style={{ fontSize: '2rem', marginBottom: '10px' }}>⏳</div>
            <p>Loading doctors...</p>
          </div>
        )}

        {/* ── HEAD DOCTOR FEATURED CARD ── */}
        {headDoctor && (
          <div
            className="featured-doctor-card"
            onClick={() => openModal(headDoctor)}
            title="Click to view full profile"
          >
            {/* Left: Large Photo */}
            <div className="featured-photo-box">
              {headDoctor.image ? (
                <img src={headDoctor.image} alt={headDoctor.name} className="featured-photo-img" />
              ) : (
                <FiUser className="featured-photo-placeholder" />
              )}
            </div>

            {/* Right: Details */}
            <div className="featured-details">
              <span className="featured-specialty-badge">{headDoctor.specialty}</span>
              <h2 className="featured-name">{headDoctor.name}</h2>
              <p className="featured-exp">{headDoctor.experience} of Clinical Experience</p>
              <p className="featured-bio">{headDoctor.bio}</p>

              <div className="featured-meta-row">
                <div className="featured-meta-item">
                  <FiMapPin />
                  <span>{headDoctor.location}</span>
                </div>
                <div className="featured-meta-item">
                  <FiGlobe />
                  <span>{(headDoctor.languages || []).join(', ')}</span>
                </div>
              </div>

              <div className="featured-conditions">
                {(headDoctor.conditions || []).map((c) => (
                  <span key={c} className="featured-condition-tag">{c}</span>
                ))}
              </div>

              <button
                className="featured-book-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/appointments', {
                    state: { doctorName: headDoctor.name, specialty: headDoctor.specialty },
                  });
                }}
              >
                Book Appointment
              </button>
            </div>
          </div>
        )}

        {/* ── SECTION LABEL ── */}
        {otherDoctors.length > 0 && (
          <>
            <div className="other-doctors-heading">
              <h3>Our Medical Team</h3>
              <div className="heading-underline" />
            </div>

            {/* ── OTHER DOCTORS GRID ── */}
            <div className="other-doctors-grid">
              {otherDoctors.map((doctor) => (
                <div
                  key={doctor.id}
                  className="doctor-card-item"
                  onClick={() => openModal(doctor)}
                >
                  <div className="doctor-card-avatar-box">
                    {doctor.image ? (
                      <img src={doctor.image} alt={doctor.name} className="doctor-card-avatar-img" />
                    ) : (
                      <FiUser className="doctor-card-avatar-placeholder" />
                    )}
                    <span className="doctor-card-badge">{doctor.specialty}</span>
                  </div>

                  <div className="doctor-card-body">
                    <h3 className="doctor-card-name">{doctor.name}</h3>
                    <p className="doctor-card-exp">{doctor.experience} Experience</p>
                    <p className="doctor-card-focus">"{doctor.focus}"</p>

                    <div className="doctor-card-meta">
                      <div className="meta-item"><FiMapPin /> <span>{doctor.location}</span></div>
                      <div className="meta-item"><FiGlobe /> <span>{(doctor.languages || []).join(', ')}</span></div>
                    </div>

                    <div className="doctor-card-actions">
                      <button
                        className="btn-appointment-book"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/appointments', {
                            state: { doctorName: doctor.name, specialty: doctor.specialty },
                          });
                        }}
                      >
                        Book Appointment
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── DOCTOR PROFILE MODAL ── */}
      {selectedDoctorModal && (
        <div className="doc-modal-backdrop" onClick={closeModal}>
          <div className="doc-modal-card" onClick={(e) => e.stopPropagation()}>
            <button className="doc-modal-close" onClick={closeModal} aria-label="Close modal">
              <FiX size={22} />
            </button>

            <div className="doc-modal-body">
              {/* Left Column */}
              <div className="doc-modal-left">
                <div className="doc-modal-avatar-box">
                  {selectedDoctorModal.image ? (
                    <img src={selectedDoctorModal.image} alt={selectedDoctorModal.name} className="doc-modal-avatar-img" />
                  ) : (
                    <FiUser size={80} className="doc-modal-avatar-placeholder" />
                  )}
                  <span className={`doc-modal-status-badge ${selectedDoctorModal.acceptingNewPatients ? 'accepting' : 'full'}`}>
                    {selectedDoctorModal.acceptingNewPatients ? 'Accepting Patients' : 'Consultations Full'}
                  </span>
                </div>

                <div className="doc-modal-quick-stats">
                  <div className="stat-item">
                    <span className="stat-label">Clinical Experience</span>
                    <span className="stat-val">{selectedDoctorModal.experience}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Consultation Room</span>
                    <span className="stat-val">{selectedDoctorModal.location}</span>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="doc-modal-right">
                <span className="doc-modal-specialty-badge">{selectedDoctorModal.specialty}</span>
                <h2 className="doc-modal-name">{selectedDoctorModal.name}</h2>

                <div className="doc-modal-section">
                  <h5>Professional Bio</h5>
                  <p className="doc-modal-bio">{selectedDoctorModal.bio}</p>
                </div>

                <div className="doc-modal-section">
                  <h5>Specialties & Conditions Treated</h5>
                  <div className="doc-modal-tags">
                    {(selectedDoctorModal.conditions || []).map((c) => (
                      <span key={c} className="doc-modal-tag">{c}</span>
                    ))}
                  </div>
                </div>

                <div className="doc-modal-section grid-two-cols">
                  <div>
                    <h5>Languages Spoken</h5>
                    <div className="doc-modal-meta-item">
                      <FiGlobe /> <span>{(selectedDoctorModal.languages || []).join(', ')}</span>
                    </div>
                  </div>
                  <div>
                    <h5>Emergency Support</h5>
                    <div className="doc-modal-meta-item">
                      <FiClock /> <span>24/7 Ward Support (Kolhapur)</span>
                    </div>
                  </div>
                </div>

                <div className="doc-modal-actions">
                  <button
                    className="doc-modal-book-btn"
                    onClick={() => {
                      closeModal();
                      navigate('/appointments', {
                        state: {
                          doctorName: selectedDoctorModal.name,
                          specialty: selectedDoctorModal.specialty,
                        },
                      });
                    }}
                  >
                    Book Appointment with {selectedDoctorModal.name.split(' ').slice(0, 2).join(' ')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorsPage;
